import React, { useCallback, useEffect, useState } from 'react';
import { Button, Popover, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAccountComputeBalance, getAccountPaymentEntitlement, type AccountPaymentEntitlement } from '../../api';
import { useAppStore } from '../../store';
import { usePaymentModals } from '../paymentModalContext';
import { ACCOUNT_PAYMENT_DATA_UPDATED_EVENT } from '../../utils/accountPaymentEvents';
import aiPointsIcon from '../../assets/images/ai_points.png';
import './MobileTopBar.css';

interface MobileTopBarProps {
  onLogout: () => void;
}

function formatComputeBalance(value: number | null) {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) {
    return '0';
  }
  return Math.max(0, numeric).toLocaleString('zh-CN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export const MobileTopBar: React.FC<MobileTopBarProps> = ({ onLogout }) => {
  const appName = useAppStore((state) => state.appConfig.appName);
  const authUser = useAppStore((state) => state.auth.user);
  const isDemoMode = useAppStore((state) => state.dataSpaceState.dataSpace === 'DEMO');
  const navigate = useNavigate();
  const location = useLocation();
  const { openMembershipPaymentModal, openComputePowerDetailModal } = usePaymentModals();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [computeBalance, setComputeBalance] = useState<number | null>(null);
  const [paymentEntitlement, setPaymentEntitlement] = useState<AccountPaymentEntitlement | null>(null);

  const loadPaymentProfile = useCallback(async () => {
    if (!authUser?.id) {
      return;
    }

    const [balancePayload, entitlementPayload] = await Promise.all([
      getAccountComputeBalance().catch(() => null),
      getAccountPaymentEntitlement().catch(() => null),
    ]);
    setComputeBalance(balancePayload?.balance ?? 0);
    setPaymentEntitlement(entitlementPayload || null);
  }, [authUser?.id]);

  useEffect(() => {
    void loadPaymentProfile();
  }, [loadPaymentProfile, location.pathname, location.search]);

  useEffect(() => {
    const handlePaymentDataUpdated = () => {
      void loadPaymentProfile();
    };
    window.addEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
    return () => window.removeEventListener(ACCOUNT_PAYMENT_DATA_UPDATED_EVENT, handlePaymentDataUpdated);
  }, [loadPaymentProfile]);

  const handleMembershipClick = () => {
    setAccountMenuOpen(false);
    if (isDemoMode) {
      message.info('当前为演示数据模式');
      return;
    }
    openMembershipPaymentModal({
      mode: paymentEntitlement?.active ? 'renew' : 'new',
      currentPlanExpireAt: paymentEntitlement?.effectiveUntil,
    });
  };

  const handleComputePowerClick = () => {
    setAccountMenuOpen(false);
    if (isDemoMode) {
      message.info('当前为演示数据模式');
      return;
    }
    openComputePowerDetailModal();
  };

  const renderAccountMenu = () => (
    <div className="mobile-top-bar__account-menu">
      <div className="mobile-top-bar__account-header">
        <div className="mobile-top-bar__account-avatar">N</div>
        <div className="mobile-top-bar__account-info">
          <strong>{authUser?.nickname || appName}</strong>
          <span>{isDemoMode ? '演示模式' : '账户管理'}</span>
        </div>
      </div>
      <div className="mobile-top-bar__account-actions">
        <Button type="text" onClick={handleMembershipClick}>会员套餐</Button>
        <Button type="text" onClick={handleComputePowerClick}>算力明细</Button>
        <Button
          type="text"
          onClick={() => {
            setAccountMenuOpen(false);
            navigate('/settings/accounts');
          }}
        >
          账户设置
        </Button>
        <Button
          type="text"
          onClick={() => {
            setAccountMenuOpen(false);
            onLogout();
          }}
        >
          退出登录
        </Button>
      </div>
    </div>
  );

  return (
    <header className="mobile-top-bar">
      <Button
        type="text"
        className="mobile-top-bar__brand"
        aria-label={`${appName}首页`}
        onClick={() => navigate('/marketing-operator')}
      >
        <span>{appName || 'FluxMind'}</span>
      </Button>
      <div className="mobile-top-bar__actions">
        <Button
          type="text"
          className="mobile-top-bar__compute"
          icon={<img src={aiPointsIcon} alt="" aria-hidden="true" />}
          onClick={handleComputePowerClick}
        >
          {formatComputeBalance(computeBalance)}
        </Button>
        <Popover
          trigger="click"
          placement="bottomRight"
          arrow={false}
          open={accountMenuOpen}
          onOpenChange={setAccountMenuOpen}
          overlayClassName="mobile-top-bar__account-popover"
          content={renderAccountMenu()}
        >
          <Button
            type="text"
            className="mobile-top-bar__account-trigger"
            aria-label="账户管理"
            icon={<UserOutlined />}
          />
        </Popover>
      </div>
    </header>
  );
};
